import React, { memo, useCallback } from 'react';
import type { StepNode, OptionsItem } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import { resolveLabel } from '../../../utils/contentResolver';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createListItemSelectEvent, createListItemDeselectEvent } from '../../../utils/eventEmitter';
import { getButtonStateFromSchema } from '../../../utils/buttonVisibility';
import { mapVerticalAlignment } from '../../../utils/styleMapper';

import { CopyBlock } from '../../shared/CopyBlock';
import { ActionBar } from '../../shared/ActionBar';
import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import { OptionsList } from '../../shared/OptionsList';
import { Media } from '../../shared/Media';
import styles from './GridMultiPick.module.css';

export interface GridMultiPickProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  selectedIndices?: Set<number>;
  onSelectionChange?: (indices: Set<number>) => void;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
}

export const GridMultiPick = memo(function GridMultiPick({
  step,
  locale,
  variables,
  resolvedScreenValues: _resolvedScreenValues,
  selectedIndices = new Set<number>(),
  onSelectionChange,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
}: GridMultiPickProps): React.ReactElement {
  const { content } = step;

  // `selected` from OptionsItem variant = NEW desired state (!currentSelected)
  const maxSelectedItemCount = content?.options?.maxSelectedItemCount;
  const handleListSelect = useCallback((_item: OptionsItem, index: number, selected: boolean) => {
    // Defense-in-depth: block adding beyond max (UI disables items, but guard anyway)
    if (selected && maxSelectedItemCount != null && selectedIndices.size >= maxSelectedItemCount) {
      return;
    }
    const newIndices = new Set(selectedIndices);
    if (selected) {
      newIndices.add(index);
      onEvent?.(createListItemSelectEvent('list-item-select', step, `list-item-${index}`));
    } else {
      newIndices.delete(index);
      onEvent?.(createListItemDeselectEvent('list-item-deselect', step, `list-item-${index}`));
    }
    onSelectionChange?.(newIndices);
  }, [selectedIndices, onSelectionChange, step, onEvent, maxSelectedItemCount]);

  // The option group is a `radiogroup`/`group`; give it the question it is asking so a
  // screen reader announces more than "group".
  const optionsGroupLabel = resolveLabel(content?.copy?.title, locale, variables) ?? undefined;

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;
  const mediaHeightPercentage = content?.featuredMedia?.appearance?.dimensions?.heightPercentage;
  const hasStaticMediaHeight = typeof mediaHeightPercentage === 'number' && mediaHeightPercentage > 0;
  const isMediaTopAligned = content?.featuredMedia?.appearance?.topAlignment === 'top' && !hasStaticMediaHeight;
  const hasSelection = selectedIndices.size > 0;
  const primaryState = getButtonStateFromSchema(content?.actionBar?.primary, hasSelection);
  const contentStyle: React.CSSProperties = {
    justifyContent: mapVerticalAlignment(content?.options?.appearance?.verticalAlignment),
  };

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${isMediaTopAligned ? ` ${styles.noPaddingTop}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-grid-multi-pick"
    >

      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      <HeaderBar
        header={content?.header}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        editPath={ep ? `${ep}.header` : undefined}
        absolute={isMediaTopAligned}
      />

      <div className={styles.content} style={contentStyle}>
        {content?.featuredMedia && (
          <Media
            media={content.featuredMedia}
            locale={locale}
            editPath={ep ? `${ep}.featuredMedia` : undefined}
            fillContainer={isMediaTopAligned}
            previewMode={previewMode}
          />
        )}
        <div className={styles.mainContent}>
          <div className={`${styles.mainContentWrapper} ${styles.xPaddings}`}>
            <CopyBlock
              copyBlock={content?.copy}
              locale={locale}
              variables={variables}
              editPath={ep ? `${ep}.copy` : undefined}
            />

            <OptionsList
              options={content?.options}
              locale={locale}
              variables={variables}
              selectedIndices={selectedIndices}
              ariaLabel={optionsGroupLabel}
              onSelect={handleListSelect}
              editPath={ep ? `${ep}.options` : undefined}
              previewMode={previewMode}
              step={step}
            />
          </div>
        </div>
      </div>

      <ActionBar
        actionBar={content?.actionBar}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        primaryState={primaryState}
        editPath={ep ? `${ep}.actionBar` : undefined}
      />
    </div>
  );
});

GridMultiPick.displayName = 'GridMultiPick';
